import type React from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import { useSectionType } from '../../sections/SectionTypeContext.tsx';
import { WidgetDefaultRequiredLogic } from './WidgetDefaultRequiredLogic.ts';

interface WidgetCreationDefaultsContextType {
  /** Nuovi input con la regola "Campo obbligatorio" già attiva */
  defaultRequired: boolean;
  /** Nuovi widget di scelta con etichetta a blocco */
  blockLabels: boolean;
  setDefaultRequired: (value: boolean) => void;
  setBlockLabels: (value: boolean) => void;
}

const WidgetCreationDefaultsContext = createContext<
  WidgetCreationDefaultsContextType | undefined
>(undefined);

interface IProps {
  children: React.ReactNode;
}

/**
 * Default di creazione dei widget nel builder (Popover "Creazione nuovi
 * Elementi"): stato locale non persistito, valido finché la pagina resta
 * aperta, applicato da `useCreateInitialWidget` ai soli widget creati da quel
 * momento. L'obbligatorietà parte attiva se la variabile d'ambiente del tipo
 * di sezione corrente (frontoffice o backoffice) è accesa; l'etichetta a
 * blocco parte sempre disattiva. Va montato dentro `SectionTypeProvider`.
 */
export function WidgetCreationDefaultsProvider(props: IProps) {
  const { sectionType } = useSectionType();
  const [defaultRequired, setDefaultRequired] = useState(() =>
    WidgetDefaultRequiredLogic.isEnabled(sectionType),
  );
  const [blockLabels, setBlockLabels] = useState(false);

  const value = useMemo(
    () => ({
      defaultRequired,
      blockLabels,
      setDefaultRequired,
      setBlockLabels,
    }),
    [defaultRequired, blockLabels],
  );

  return (
    <WidgetCreationDefaultsContext.Provider value={value}>
      {props.children}
    </WidgetCreationDefaultsContext.Provider>
  );
}

/**
 * Fallback per i consumer renderizzati fuori dal provider: replica
 * l'attivazione via sola variabile d'ambiente (comportamento precedente al
 * Popover), con setter inerti e identità stabile tra i render. Due costanti
 * a livello di modulo perché l'attivazione dipende dal tipo di sezione.
 */
function createFallbackCreationDefaults(
  defaultRequired: boolean,
): WidgetCreationDefaultsContextType {
  return {
    defaultRequired,
    blockLabels: false,
    setDefaultRequired: () => {},
    setBlockLabels: () => {},
  };
}

const enabledFallback = createFallbackCreationDefaults(true);
const disabledFallback = createFallbackCreationDefaults(false);

export const useWidgetCreationDefaults =
  (): WidgetCreationDefaultsContextType => {
    const context = useContext(WidgetCreationDefaultsContext);
    const { sectionType } = useSectionType();
    if (context) return context;
    return WidgetDefaultRequiredLogic.isEnabled(sectionType)
      ? enabledFallback
      : disabledFallback;
  };
